import { Controller } from "@hotwired/stimulus"
import type { Root } from "react-dom/client"
import type { Coordinates, Property, Viewport } from "../entrypoints/components/PropertyMap"

type ReactModules = {
  createElement: typeof import("react").createElement
  PropertyMap: typeof import("../entrypoints/components/PropertyMap").default
}

export default class extends Controller<HTMLElement> {
  static targets = ["map"]

  static values = {
    properties: Array,
    apiKey: String,
    editable: Boolean,
    fitBounds: Boolean,
    zoomLocked: Boolean,
    fixedZoom: Number,
    latitudeSelector: String,
    longitudeSelector: String,
    zoomSelector: String,
    centerLatitudeSelector: String,
    centerLongitudeSelector: String,
    prefectureSelector: String,
    citySelector: String,
    addressSelector: String,
  }

  declare readonly propertiesValue: Property[]
  declare readonly apiKeyValue: string
  declare readonly editableValue: boolean
  declare readonly fitBoundsValue: boolean
  declare readonly zoomLockedValue: boolean
  declare readonly fixedZoomValue: number
  declare readonly latitudeSelectorValue: string
  declare readonly longitudeSelectorValue: string
  declare readonly zoomSelectorValue: string
  declare readonly centerLatitudeSelectorValue: string
  declare readonly centerLongitudeSelectorValue: string
  declare readonly prefectureSelectorValue: string
  declare readonly citySelectorValue: string
  declare readonly addressSelectorValue: string
  declare readonly mapTarget: HTMLElement

  private root?: Root
  private reactModules?: ReactModules
  private inputElements: HTMLInputElement[] = []
  private resizeToken = 0
  private geocodingToken = 0

  async connect() {
    window.addEventListener("form-steps:step-shown", this.handleStepShown)
    const [{ createElement }, { createRoot }, { default: PropertyMap }] = await Promise.all([
      import("react"),
      import("react-dom/client"),
      import("../entrypoints/components/PropertyMap"),
    ])

    if (!this.element.isConnected) return

    this.reactModules = { createElement, PropertyMap }
    this.root = createRoot(this.mapTarget)
    this.inputElements = this.selectorValues()
      .map((selector) => this.findInput(selector))
      .filter((input): input is HTMLInputElement => Boolean(input))
    this.inputElements.forEach((input) => input.addEventListener("input", this.renderMap))
    this.renderMap()
  }

  disconnect() {
    window.removeEventListener("form-steps:step-shown", this.handleStepShown)
    this.inputElements.forEach((input) => input.removeEventListener("input", this.renderMap))
    this.inputElements = []
    this.root?.unmount()
    this.root = undefined
    this.reactModules = undefined
  }

  private renderMap = () => {
    if (!this.root || !this.reactModules) return

    const { createElement, PropertyMap } = this.reactModules
    this.root.render(createElement(PropertyMap, {
      properties: this.propertiesValue,
      apiKey: this.apiKeyValue,
      editable: this.editableValue,
      fitBounds: this.fitBoundsValue,
      zoomLocked: this.zoomLockedValue,
      position: this.readCoordinates(this.latitudeSelectorValue, this.longitudeSelectorValue),
      center: this.readCoordinates(this.centerLatitudeSelectorValue, this.centerLongitudeSelectorValue),
      // fixed-zoom を指定した地図は入力欄の倍率を見ない（確認用は場所が分かる倍率で固定したい）。
      zoom: this.fixedZoomValue > 0 ? this.fixedZoomValue : this.readNumber(this.zoomSelectorValue),
      resizeToken: this.resizeToken,
      geocodingAddress: this.geocodingAddress(),
      geocodingToken: this.geocodingToken,
      onPositionChange: this.updatePosition,
      onViewportChange: this.updateViewport,
    }))
  }

  private handleStepShown = (event: Event) => {
    const step = (event as CustomEvent<{ step?: HTMLElement }>).detail.step
    if (!step?.contains(this.element)) return

    this.resizeToken += 1
    if (this.coordinatesAreEmpty() && this.geocodingAddress()) {
      this.geocodingToken += 1
    }
    this.renderMap()
  }

  refetch() {
    if (!this.geocodingAddress()) return

    this.geocodingToken += 1
    this.renderMap()
  }

  private updatePosition = ({ lat, lng }: Coordinates) => {
    this.writeInput(this.latitudeSelectorValue, lat)
    this.writeInput(this.longitudeSelectorValue, lng)
  }

  private updateViewport = ({ lat, lng, zoom }: Viewport) => {
    this.writeInput(this.centerLatitudeSelectorValue, lat)
    this.writeInput(this.centerLongitudeSelectorValue, lng)
    this.writeInput(this.zoomSelectorValue, zoom)
  }

  private readCoordinates(latitudeSelector: string, longitudeSelector: string): Coordinates | undefined {
    const lat = this.readNumber(latitudeSelector)
    const lng = this.readNumber(longitudeSelector)
    return lat === undefined || lng === undefined ? undefined : { lat, lng }
  }

  private readNumber(selector: string): number | undefined {
    const value = this.findInput(selector)?.value
    if (!value) return undefined

    const number = Number(value)
    return Number.isFinite(number) ? number : undefined
  }

  private writeInput(selector: string, value: number) {
    const input = this.findInput(selector)
    const nextValue = String(value)
    if (!input || input.value === nextValue) return

    input.value = nextValue
    input.dispatchEvent(new Event("input", { bubbles: true }))
  }

  private findInput(selector: string): HTMLInputElement | null {
    return selector ? document.querySelector<HTMLInputElement>(selector) : null
  }

  private selectorValues() {
    return [
      this.latitudeSelectorValue,
      this.longitudeSelectorValue,
      this.zoomSelectorValue,
      this.centerLatitudeSelectorValue,
      this.centerLongitudeSelectorValue,
    ].filter(Boolean)
  }

  private geocodingAddress() {
    return [ this.prefectureSelectorValue, this.citySelectorValue, this.addressSelectorValue ]
      .map((selector) => this.findInput(selector)?.value)
      .filter(Boolean)
      .join("")
  }

  private coordinatesAreEmpty() {
    return this.findInput(this.latitudeSelectorValue)?.value === "" && this.findInput(this.longitudeSelectorValue)?.value === ""
  }
}
